import { cn } from "@/shared/lib/utils";
import { FINISH_CLASS, resolveFinish } from "@/shared/lib/tier-finish";

/**
 * A warranty tier, rendered as itself.
 *
 * The backend sends a `finish` token from a closed vocabulary
 * (`WarrantyCategory.TierFinish`); this component maps it to a stylesheet
 * class. It never receives a colour, so no tenant's palette is hardcoded here.
 * An unknown or missing token falls back to `neutral`, which is the plain badge
 * every tenant had before tiers existed.
 *
 * Lives in `shared/` rather than `admin/` on purpose. Only admin surfaces use
 * it today, but the consumer portal and the warranty certificate are the
 * intended homes once OC approve customer-facing tier branding - putting it
 * here now means that pickup costs nothing.
 */

export function TierBadge({
  name,
  finish,
  size = "chip",
  className,
}: {
  /** The tier's display name, e.g. "Platinum". Comes from the API. */
  name: string;
  /** Finish token from `WarrantyCategory.finish`. */
  finish?: string | null;
  /**
   * `chip` for tables, menus, inline references - stays legible at row height.
   * `badge` for a record field, where it can carry a little more weight.
   */
  size?: "chip" | "badge";
  className?: string;
}) {
  if (!name) return null;

  return (
    <span
      className={cn(
        "tier-finish inline-flex shrink-0 items-center rounded-[5px] font-medium leading-none tracking-[0.01em] whitespace-nowrap",
        size === "chip"
          ? "px-2 py-1 text-[11px]"
          : "px-2.5 py-1.5 text-[12.5px]",
        FINISH_CLASS[resolveFinish(finish)],
        className,
      )}
    >
      <span className="relative">{name}</span>
    </span>
  );
}

/**
 * The pair shown after a tier change: where it came from, where it landed.
 *
 * Used by the success message so an agent sees the move rather than reading a
 * sentence about it. The old tier is de-emphasised so the eye lands on the new
 * one first.
 */
export function TierChange({
  from,
  to,
  fromFinish,
  toFinish,
  className,
}: {
  from: string;
  to: string;
  fromFinish?: string | null;
  toFinish?: string | null;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <TierBadge
        name={from}
        finish={fromFinish}
        className="opacity-55 saturate-[0.6]"
      />
      <svg
        width="16"
        height="9"
        viewBox="0 0 16 9"
        fill="none"
        aria-hidden="true"
        className="shrink-0 text-[var(--ink-3)]"
      >
        <path
          d="M0 4.5h13M10.5 1 14 4.5 10.5 8"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <TierBadge name={to} finish={toFinish} />
    </span>
  );
}

/**
 * A small square of a tier's finish, for places that reference a tier without
 * room for its name - the destination beside an "Upgrade to Platinum" menu
 * item, for instance, where the label already says which tier it is.
 */
export function TierSwatch({
  finish,
  className,
}: {
  finish?: string | null;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "tier-finish tier-swatch inline-block h-3.5 w-3.5 shrink-0 rounded-[3px]",
        FINISH_CLASS[resolveFinish(finish)],
        className,
      )}
    />
  );
}
